import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Button, WithTooltip } from 'basic-components';
import { AddParameter, Box, File, Play, Redo, Refresh, Stop, TextIcon, Undo } from 'basic-components/icons';

import { When } from 'react-if';

import './Controls.scss';

const Controls = ({ canResetFile, editorRef = undefined, fileContent = undefined, hasInitializationError = false, interruptExecution, isFilePending, isLoading = false, isReady, isRunning, isWrapEnabled, resetFileContent, restartKernel, runPython, setAreParamsOpen, setIsAddPackageOpen, setIsSnippetsOpen, setIsWrapEnabled, taskId = undefined }) => {
  const [isRedoAvailable, setIsRedoAvailable] = useState(false),
        [isUndoAvailable, setIsUndoAvailable] = useState(false),
        isRestartBlocked = isLoading || isRunning || (!isReady && !hasInitializationError),
        updateUndoRedoButtons = useCallback(() => {
          if(editorRef.current) {
            const undoManager = editorRef.current.editor.session.getUndoManager()
            setIsRedoAvailable(undoManager.hasRedo())
            setIsUndoAvailable(undoManager.hasUndo())
          }
        }, [editorRef]),
        onUndo = useCallback(() => {
          if(isUndoAvailable) {
            editorRef.current?.editor?.undo?.()
            updateUndoRedoButtons()
          }
        }, [editorRef, isUndoAvailable, updateUndoRedoButtons]),
        onRedo = useCallback(() => {
          if(isRedoAvailable) {
            editorRef.current?.editor?.redo?.()
            updateUndoRedoButtons()
          }
        }, [editorRef, isRedoAvailable, updateUndoRedoButtons]),
        onRun = useCallback(() => runPython(fileContent), [fileContent, runPython]),
        onInterrupt = useCallback(() => interruptExecution(), [interruptExecution]),
        onRestart = useCallback(() => {
          if(!isRestartBlocked) {
            restartKernel()
          }
        }, [isRestartBlocked, restartKernel]),
        onReset = useCallback(() => resetFileContent(), [resetFileContent]),
        onOpenAddPackage = useCallback(() => setIsAddPackageOpen(true), [setIsAddPackageOpen]),
        onOpenSnippets = useCallback(() => setIsSnippetsOpen(true), [setIsSnippetsOpen]),
        onOpenParams = useCallback(() => setAreParamsOpen(true), [setAreParamsOpen]),
        onToggleWrap = useCallback(() => setIsWrapEnabled(enabled => !enabled), [setIsWrapEnabled])

  useEffect(() => {
    if(editorRef.current) {
      editorRef.current.editor.session.on("change", updateUndoRedoButtons)
      editorRef.current.editor.commands.on("afterExec", updateUndoRedoButtons)
    }
  }, [editorRef, updateUndoRedoButtons])

  return (
    <div className="ng-python-editor__footer__controls">
      <WithTooltip
          text="Undo change"
      >
        <div 
            className="ng-python-editor__footer__controls__control"
            onClick={onUndo}
        >
          <Undo
              color={isUndoAvailable ? "blue-bright" : "grey-base"}
              height={16}
              width={16}
          />
        </div>
      </WithTooltip>
      <WithTooltip
          text="Redo change"
      >
        <div 
            className="ng-python-editor__footer__controls__control"
            onClick={onRedo}
        >
          <Redo
              color={isRedoAvailable ? "blue-bright" : "grey-base"}
              height={16}
              width={16}
          />
        </div>
      </WithTooltip>
      <div className="ng-python-editor__footer__controls__separator"/>
      <WithTooltip
          text="Run code in sandbox"
      >
        <div className="ng-python-editor__footer__controls__button">
          <Button 
              disabled={isRunning || !isReady}
              onClick={onRun}
              size="small"
              variant="text"
          >
            <Play
                color={isRunning || !isReady ? "grey-base" : "blue-bright"}
                height={16}
                width={16}
            />
            Run
          </Button>
        </div>
      </WithTooltip>
      <WithTooltip
          text="Terminate code execution"
      >
        <div className="ng-python-editor__footer__controls__button">
          <Button 
              disabled={!isRunning || !isReady}
              onClick={onInterrupt}
              size="small"
              variant="text"
          >
            <Stop
                color={isRunning && isReady ? "blue-bright" : "grey-base"}
                height={16}
                width={16}
            />
            Terminate
          </Button>
        </div>
      </WithTooltip>
      <div className="ng-python-editor__footer__controls__separator"/>
      <WithTooltip
          text="Restart environment"
      >
        <div className="ng-python-editor__footer__controls__button">
          <Button 
              aria-busy={isLoading}
              disabled={isRestartBlocked}
              onClick={onRestart}
              size="small"
              variant="text"
          >
            <Refresh
                color={isRestartBlocked ? "grey-base" : "blue-bright"}
                height={16}
                width={16}
            />
            Restart
          </Button>
        </div>
      </WithTooltip>
      <WithTooltip text="Reset code to the original file content">
        <div className="ng-python-editor__footer__controls__button">
          <Button
              disabled={!canResetFile || isFilePending || !isUndoAvailable}
              onClick={onReset}
              size="small"
              variant="text"
          >
            <File
                color={canResetFile && !isFilePending && isUndoAvailable ? "blue-bright" : "grey-base"}
                height={16}
                width={16}
            />
            Reset file
          </Button>
        </div>
      </WithTooltip>
      <div className="ng-python-editor__footer__controls__separator"/>
      <WithTooltip
          text="Add package"
      >
        <div 
            className="ng-python-editor__footer__controls__control"
            onClick={onOpenAddPackage}
            style={{ height: 20, width: 20 }}
        >
          <Box
              color="blue-bright"
              height={20}
              width={20}
          />
        </div>
      </WithTooltip>
      <WithTooltip
          text="Insert snippet"
      >
        <div 
            className="ng-python-editor__footer__controls__control"
            onClick={onOpenSnippets}
        >
          <TextIcon
              color="blue-bright"
              height={16}
              width={16}
          />
        </div>
      </WithTooltip>
      <When condition={!!taskId}>
        {() => (
          <WithTooltip
              text="Change params"
          >
            <div 
                className="ng-python-editor__footer__controls__control"
                onClick={onOpenParams}
            >
              <AddParameter
                  color="blue-bright"
                  height={16}
                  width={16}
              />
            </div>
          </WithTooltip>
        )}
      </When>
      <div className="ng-python-editor__footer__controls__note">
        {taskId ? "Current task parameters are automatically injected." : ""}
      </div>
      <div className="ng-python-editor__footer__controls__separator"/>
      <WithTooltip
          text={isWrapEnabled ? "Disable text code" : "Enable text code"}
      >
        <div className="ng-python-editor__footer__controls__button">
          <Button 
              onClick={onToggleWrap}
              size="small"
              variant="text"
          >
            <TextIcon
                color="blue-bright"
                height={16}
                width={16}
            />
            {isWrapEnabled ? "Unwrap code" : "Wrap code"}
          </Button>
        </div>
      </WithTooltip>
    </div>
  )
}

Controls.propTypes = {
  canResetFile: PropTypes.bool.isRequired,
  editorRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any })
  ]),
  fileContent: PropTypes.string,
  hasInitializationError: PropTypes.bool,
  interruptExecution: PropTypes.func.isRequired,
  isFilePending: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool,
  isReady: PropTypes.bool.isRequired,
  isRunning: PropTypes.bool.isRequired,
  isWrapEnabled: PropTypes.bool.isRequired,
  resetFileContent: PropTypes.func.isRequired,
  restartKernel: PropTypes.func.isRequired,
  runPython: PropTypes.func.isRequired,
  setAreParamsOpen: PropTypes.func.isRequired,
  setIsAddPackageOpen: PropTypes.func.isRequired,
  setIsSnippetsOpen: PropTypes.func.isRequired,
  setIsWrapEnabled: PropTypes.func.isRequired,
  taskId: PropTypes.string,
}

export default Controls;
